import { NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  Component,
  computed,
  contentChild,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  model,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import { MatPseudoCheckbox } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { EFindingCardSortMode, IFindingCardData, IFindingCardSortOption } from '../finding-card.interface';
import { FindingCardComponent } from '../finding-card/finding-card.component';
import { getScrollParent } from './finding-card-grid.util';

@Component({
  imports: [NgTemplateOutlet, MatPseudoCheckbox, TranslateModule, NgxSkeletonLoaderModule, FindingCardComponent],
  selector: 'app-finding-card-grid',
  templateUrl: './finding-card-grid.component.html',
})
export class FindingCardGridComponent<T extends IFindingCardData> {
  // Cards are rendered in batches so long lists (1000+ findings) stay cheap to render and change-detect;
  // the next batch is added when the end of the rendered cards nears the visible part of the scroll container.
  private static readonly BATCH_SIZE = 20;

  // Expected already sorted by the parent (it needs the same order for the objective jump action).
  public readonly cards = input.required<T[]>();
  public readonly sortMode = model<EFindingCardSortMode>(EFindingCardSortMode.time);
  public readonly selectedId = model<string | null>(null);
  public readonly dimmedIds = input<ReadonlySet<string>>(new Set());
  public readonly loading = input<boolean>(false);
  public readonly canLoadImages = input<boolean>(false);
  public readonly imageCampaignExportId = input<string | null>(null);
  public readonly aspectRatio = input<string>('16 / 9');
  public readonly minCardWidth = input<number>(220);
  public readonly numberColor = input<string | null>(null);

  public readonly openOnPlatform = output<{ event: Event; card: T }>();

  public readonly actionsTemplate = contentChild<TemplateRef<{ $implicit: T }>>(TemplateRef);

  public readonly renderedCount = signal(FindingCardGridComponent.BATCH_SIZE);
  public readonly renderedCards = computed(() => this.cards().slice(0, this.renderedCount()));
  public readonly hasMoreCards = computed(() => this.renderedCount() < this.cards().length);

  public readonly skeletonCards = [1, 2, 3];
  public readonly sortOptions: IFindingCardSortOption[] = [
    { labelKey: 'pages.campaignDetails.ctaAnalytics.details.findingCards.sortTime', value: EFindingCardSortMode.time },
    {
      labelKey: 'pages.campaignDetails.ctaAnalytics.details.findingCards.sortLongestVisible',
      value: EFindingCardSortMode.longestVisible,
    },
  ];

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);
  private readonly loadMoreSentinel = viewChild<ElementRef<HTMLElement>>('loadMoreSentinel');

  constructor() {
    effect(() => {
      this.sortMode();
      untracked(() => this.renderedCount.set(FindingCardGridComponent.BATCH_SIZE));
    });

    // Re-observed after every batch: IntersectionObserver only reports changes, so a sentinel that is still
    // in range after a batch renders (tall screens) would otherwise never trigger the next one.
    effect((onCleanup) => {
      const sentinel = this.loadMoreSentinel()?.nativeElement;

      this.renderedCount();

      if (!sentinel) {
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.renderedCount.update((count) => count + FindingCardGridComponent.BATCH_SIZE);
          }
        },
        // The scroll container as root: with the viewport as root, rootMargin can't reach past the clipping
        // of the dialog's scrolling panel, so the next batch would only render once the sentinel is visible.
        { root: getScrollParent(sentinel), rootMargin: '400px' },
      );

      observer.observe(sentinel);
      onCleanup(() => observer.disconnect());
    });
  }

  public onOpenOnPlatform(event: Event, card: T): void {
    this.selectedId.set(card.id);
    this.openOnPlatform.emit({ card, event });
  }

  // A click anywhere in the grid except on the highlighted card clears the highlight. "Open on platform"
  // has already selected its own card by the time its click could bubble up here.
  public onGridClick(event: MouseEvent): void {
    const selectedId = this.selectedId();

    if (!selectedId) {
      return;
    }

    const clickedCard = (event.target as HTMLElement).closest<HTMLElement>('[data-finding-id]');

    if (clickedCard?.dataset['findingId'] !== selectedId) {
      this.selectedId.set(null);
    }
  }

  // Renders up to the target card's batch first, so jumping to a card that isn't rendered yet still works.
  public scrollToCard(cardId: string): void {
    const index = this.cards().findIndex((card) => card.id === cardId);

    if (index === -1) {
      return;
    }

    const scroll = (): void =>
      this.elementRef.nativeElement
        .querySelector(`[data-finding-id="${CSS.escape(cardId)}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    if (index < this.renderedCount()) {
      scroll();

      return;
    }

    const batchSize = FindingCardGridComponent.BATCH_SIZE;

    this.renderedCount.set(Math.ceil((index + 1) / batchSize) * batchSize);
    afterNextRender(scroll, { injector: this.injector });
  }
}
